/**
 * Settings → Notifications (mobile M6).
 *
 * Two halves. **This device**: turn notifications on or off here (each phone
 * and browser separately), and send a test. **What notifies**: a switch per
 * event, and whether a notification may show an amount — off by default,
 * because notifications pass through Google's or Apple's servers and show on
 * the lock screen (the owner's answer to plan Q5).
 */

import * as SwitchPrimitive from "@radix-ui/react-switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Send } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button, ErrorState, Panel, Skeleton } from "@/components/ui/primitives";
import { pushApi, type PushConfig, type PushEvent } from "@/lib/api/client";
import { deviceName } from "@/platform";
import { disablePush, enablePush, pushUnavailableReason, registeredTarget } from "@/platform/notifications";

const KEY = ["push", "config"] as const;

export function NotificationsPanel() {
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: KEY, queryFn: pushApi.config });

  return (
    <Panel className="overflow-hidden">
      <div className="p-5">
        <h2 className="font-display text-base font-medium text-ink">Notifications</h2>
        <p className="mt-1 max-w-prose text-sm text-muted">
          MyDeck can tell you when something needs you. Each phone and browser is turned on separately.
        </p>
      </div>
      {isLoading ? (
        <div className="space-y-3 border-t border-hairline px-5 py-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError || !data ? (
        <div className="border-t border-hairline">
          <ErrorState message={(error as Error)?.message ?? "Couldn't load."} onRetry={() => refetch()} />
        </div>
      ) : (
        <>
          <ThisDevice config={data} />
          <Switches config={data} />
        </>
      )}
    </Panel>
  );
}

function ThisDevice({ config }: { config: PushConfig }) {
  const qc = useQueryClient();
  const [on, setOn] = React.useState(() => registeredTarget() !== null);
  const [busy, setBusy] = React.useState(false);
  const unavailable = pushUnavailableReason();
  const serverReady = config.fcm || config.webpush_public_key !== null;

  const test = useMutation({
    mutationFn: pushApi.test,
    onSuccess: () => toast.success("Sent. It should arrive within a minute."),
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = async (next: boolean) => {
    setBusy(true);
    try {
      if (next) {
        const label = (await deviceName()) ?? undefined;
        const why = await enablePush(config.webpush_public_key, (kind, target) =>
          pushApi.subscribe(kind, target, label),
        );
        if (why) toast.error(why);
        else setOn(true);
      } else {
        await disablePush(pushApi.unsubscribe);
        setOn(false);
      }
      void qc.invalidateQueries({ queryKey: KEY });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-hairline px-5 py-4">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-hairline bg-raised text-muted">
          <BellRing className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <label htmlFor="push-here" className="text-sm text-ink">
            Notifications on this device
          </label>
          <p className="mt-0.5 text-xs text-muted">
            {!serverReady
              ? "Not set up on the server yet — see docs/DEPLOY.md, “Notifications”."
              : (unavailable ?? `${config.subscriptions} device${config.subscriptions === 1 ? "" : "s"} receiving them.`)}
          </p>
        </div>
        <Toggle id="push-here" checked={on} disabled={busy || !serverReady || unavailable !== null} onChange={toggle} />
      </div>
      {on && serverReady && (
        <Button variant="ghost" size="sm" className="mt-3" disabled={test.isPending} onClick={() => test.mutate()}>
          <Send className="h-3.5 w-3.5" aria-hidden />
          Send a test
        </Button>
      )}
    </div>
  );
}

function Switches({ config }: { config: PushConfig }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: pushApi.preferences,
    onSuccess: (next) => qc.setQueryData(KEY, next),
    onError: (e: Error) => toast.error(e.message),
  });
  const events = Object.keys(config.event_labels) as PushEvent[];

  return (
    <div className="border-t border-hairline">
      <h3 className="label-micro px-5 pb-1 pt-4">What notifies</h3>
      <ul>
        {events.map((ev) => (
          <li key={ev} className="flex min-h-12 items-center gap-3 px-5 py-2">
            <label htmlFor={`push-${ev}`} className="min-w-0 flex-1 text-sm text-ink">
              {config.event_labels[ev]}
            </label>
            <Toggle
              id={`push-${ev}`}
              checked={config.events[ev]}
              disabled={save.isPending}
              onChange={(v) => save.mutate({ events: { [ev]: v } })}
            />
          </li>
        ))}
        <li className="flex min-h-12 items-center gap-3 border-t border-hairline px-5 py-3">
          <div className="min-w-0 flex-1">
            <label htmlFor="push-amounts" className="text-sm text-ink">
              Show amounts
            </label>
            <p className="mt-0.5 text-xs text-muted">
              Off: “New transaction on your card”. On: “₹1,240.00 at a shop”. Notifications show on the lock screen.
            </p>
          </div>
          <Toggle
            id="push-amounts"
            checked={config.show_amounts}
            disabled={save.isPending}
            onChange={(v) => save.mutate({ show_amounts: v })}
          />
        </li>
      </ul>
    </div>
  );
}

function Toggle({
  id,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <SwitchPrimitive.Root
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
      className="relative h-7 w-12 shrink-0 rounded-full border border-hairline bg-raised transition-colors data-[state=checked]:border-brass/50 data-[state=checked]:bg-brass/30 disabled:opacity-50"
    >
      <SwitchPrimitive.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-ink shadow transition-transform data-[state=checked]:translate-x-[1.375rem]" />
    </SwitchPrimitive.Root>
  );
}
